import type { StatusTone } from '@/shared/ui/status-badge'

interface StatusMeta {
  label: string
  tone: StatusTone
}

const fallback = (status: string): StatusMeta => ({ label: status || '—', tone: 'muted' })

// Словарь статусов заявки — ровно перечисление домена fleet. `partial` красится
// рабочим тоном намеренно: часть слотов у потребителя уже есть, и он вправе
// работать на них сколько угодно — это не авария. Ноль доступных слотов
// (`unfulfilled`) терминалом тоже не является: ядро добирает каждым тиком.
const reservationStatuses: Record<string, StatusMeta> = {
  scheduled: { label: 'Запланирована', tone: 'info' },
  waiting_lines: { label: 'Ждёт линий', tone: 'warn' },
  warming: { label: 'Прогрев', tone: 'info' },
  ready: { label: 'Готова', tone: 'ok' },
  partial: { label: 'Частично', tone: 'accent' },
  unfulfilled: { label: 'Слотов нет', tone: 'danger' },
  releasing: { label: 'Отзывается', tone: 'warn' },
  released: { label: 'Отозвана', tone: 'muted' },
  cancelled: { label: 'Отменена', tone: 'muted' },
}

export function reservationStatus(status: string): StatusMeta {
  return reservationStatuses[status] ?? fallback(status)
}

const TERMINAL = new Set(['released', 'cancelled'])

// Терминал заявке ставит только release: ни просрочка, ни простой её не
// закрывают. Закрытая заявка не открывается — повтор работы это новая заявка.
export function reservationTerminal(status: string): boolean {
  return TERMINAL.has(status)
}

// Заявка без железа под собой — оператору она ничего не стоит.
export function reservationFree(status: string): boolean {
  return status === 'scheduled' || status === 'waiting_lines'
}

// Что статус означает для оператора одной фразой. Пара «прогрев идёт» /
// «прогрев не удался» на бейдже неразличима, а действия за ними разные.
const meanings: Record<string, string> = {
  scheduled: 'Окно ещё не подошло — ни одного сервера под заявку не заказано.',
  waiting_lines: 'Линии пула держит сосед. Железо не заказывается: ожидание бесплатно.',
  warming: 'Серверы заказаны и едут. С этого момента заявка тратит деньги.',
  ready: 'Все заказанные слоты покрыты — потребитель темпит обзвон по ним.',
  partial: 'Часть слотов уже есть. Рабочее состояние: на них можно звонить сколько угодно.',
  unfulfilled: 'Доступных слотов ноль. Заявка жива — ядро добирает ёмкость каждым тиком.',
  releasing: 'Отзыв идёт по шагам: дренаж → тишина → аренды → серверы → очередь.',
  released: 'Железо снесено, очередь закрыта, идентификатор занят навсегда.',
  cancelled: 'Закрыта до железа — сносить было нечего.',
}

export function reservationMeaning(status: string): string {
  return meanings[status] ?? ''
}

export type ReadyAtKind = 'estimate' | 'not-ours' | 'closed' | 'unknown'

/**
 * Чем является пустой `ready_at`. У ждущей чужих линий заявки это честный
 * ответ «зависит не от нас», а не отсутствие данных, и путать их дорого:
 * первое ждут, второе чинят.
 */
export function readyAtKind(status: string, readyAt: string | undefined): ReadyAtKind {
  if (readyAt) return 'estimate'
  if (status === 'waiting_lines') return 'not-ours'
  if (reservationTerminal(status)) return 'closed'
  return 'unknown'
}

const poolStates: Record<string, StatusMeta> = {
  cold: { label: 'Железа нет', tone: 'muted' },
  warming: { label: 'Греется', tone: 'info' },
  ready: { label: 'Есть слоты', tone: 'ok' },
}

export function poolCapacityState(state: string): StatusMeta {
  return poolStates[state] ?? fallback(state)
}
